import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { issueGameToken } from '../lib/api/game';
import type { GameSession } from '../lib/api/game';
import { useEventState } from '../lib/eventState';
import { Button } from '../components/Button';

type LocationState = { session?: GameSession };

type GameWindow = Window & {
  GH_PLATFORM_CONFIG?: { baseUrl: string; exitUrl?: string };
};

/**
 * Chrome-less full-bleed wrapper that hosts the vendored game build in an
 * iframe and hands off the GameSession via postMessage.
 *
 * Reached only from PlayPage after the auth/phase/consent/admin gates pass
 * and a session has been minted. A direct visit / hard refresh has no
 * session in router state, so the page bounces back to /play and lets the
 * gate re-run from the top — single source of truth for gating stays in
 * PlayPage.
 *
 * Handoff contract (authoritative spec: .claude/GAME_INTEGRATION_PLAN.md):
 *   1. Same-origin direct assignment of window.GH_PLATFORM_CONFIG.baseUrl
 *      on the iframe — read lazily by the game's apiRequest() on each call.
 *   2. JSON-stringified { type: 'GameSession', data: {...} } postMessage —
 *      received by the game's listener in jakes-jingle-quest/src/scene.js.
 *      On receipt the game auto-fetches the stats catalog and bank balance.
 *
 * Session recovery contract (game -> host): when game auth dies for good
 * (refresh JWT expired after a long idle, session invalidated/superseded,
 * session row swept) the game posts { type: 'RequestGameSession' }. We mint
 * a fresh session and post it back; the game resumes and resubmits any held
 * runs. On any mint failure we bounce to /play — the gate owns the login /
 * phase / consent UX for every failure mode.
 */
export default function GamePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const session = (location.state as LocationState | null)?.session;

  // Shared, auto-refreshing event state (provider schedules a refetch at each
  // week/phase boundary + revalidates on tab refocus). Drives the proactive
  // weekly-boundary eject below.
  const eventState = useEventState();
  const [weekEnded, setWeekEnded] = useState(false);

  // Latest session handed to the game. Seeded with the gate-minted session
  // from router state; replaced on every re-mint. NEVER re-post the original
  // snapshot after the game has been running — its tokens rotate server-side
  // as the game refreshes, and a stale re-injection is exactly the
  // token-clobber bug the game guards against.
  const latestSessionRef = useRef<GameSession | undefined>(session);
  const firstLoadDoneRef = useRef(false);
  const mintInFlightRef = useRef(false);

  useEffect(() => {
    applyPageMeta(PAGE_META.playGame);
  }, []);

  useEffect(() => {
    if (!session) navigate('/play', { replace: true });
  }, [session, navigate]);

  const postSessionToGame = useCallback((s: GameSession) => {
    const win = iframeRef.current?.contentWindow as GameWindow | null;
    if (!win) return;

    // exitUrl: the game's exitToGamerhood() reads cfg.exitUrl from the injected
    // config (platformBridge.js) and falls back to its hardcoded GAMERHOOD_EXIT_URL.
    // Point it at the site root so "exit" returns the player to our home page.
    win.GH_PLATFORM_CONFIG = {
      baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
      exitUrl: '/',
    };

    win.postMessage(
      JSON.stringify({
        type: 'GameSession',
        data: {
          gameToken: s.gameToken,
          gameRefreshToken: s.gameRefreshToken,
          sessionKey: s.sessionKey,
          sessionId: s.sessionId,
          weekNumber: s.weekNumber,
        },
      }),
      window.location.origin,
    );
  }, []);

  // Mint a fresh session and hand it to the game. Single-flight: the game
  // re-posts RequestGameSession on an interval while waiting, and every mint
  // supersedes the prior session server-side — stacked mints would invalidate
  // each other. apiFetch already retries the wrapper auth refresh under the
  // hood, so any failure here means the gate needs to re-run.
  const mintAndPostSession = useCallback(async () => {
    if (mintInFlightRef.current) return;
    mintInFlightRef.current = true;
    try {
      const result = await issueGameToken();
      if (result.kind === 'ok') {
        latestSessionRef.current = result.session;
        postSessionToGame(result.session);
      } else {
        navigate('/play', { replace: true });
      }
    } catch {
      navigate('/play', { replace: true });
    } finally {
      mintInFlightRef.current = false;
    }
  }, [navigate, postSessionToGame]);

  // Game→host signals. Origin lock guards against any embedder posting in.
  // The game sends JSON-stringified { type, data } via window.parent.postMessage
  // (platformBridge.js sendBackendMessage). On ExitToGamerhood we navigate the
  // top-level SPA to home — this unmounts the iframe, vs. the game's own
  // window.location.href which would only navigate inside the iframe.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      let msg: { type?: string } | null = null;
      try {
        msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (msg?.type === 'ExitToGamerhood') navigate('/');
      if (msg?.type === 'RequestGameSession') void mintAndPostSession();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate, mintAndPostSession]);

  // Proactive weekly-boundary eject. A game session is pinned to the week it
  // was minted in; a new week requires fresh rules consent, and a run can no
  // longer count once its week has closed (the server hard-rejects late
  // submits). So the instant the active week rolls PAST the session's week — or
  // the event ends — we stop the game and route the player back through /play,
  // which sends them to /weekly-rules for the new week (or the wrap notice at
  // POST_EVENT). The provider flips within seconds of the boundary with no
  // manual refresh; a backgrounded tab ejects the moment it's refocused. This
  // catches players sitting in the lobby OR mid-run, unlike a submit-time check
  // which would let them finish and start another game on the old week.
  useEffect(() => {
    if (weekEnded) return;
    const sessionWeek = latestSessionRef.current?.weekNumber ?? null;
    const rolledPastWeek =
      eventState.currentWeek != null &&
      sessionWeek != null &&
      eventState.currentWeek > sessionWeek;
    if (eventState.phase === 'POST_EVENT' || rolledPastWeek) {
      setWeekEnded(true);
    }
  }, [eventState.phase, eventState.currentWeek, weekEnded]);

  // Once ejected, auto-route to /play after a short beat so the player isn't
  // stranded on the interstitial. The gate re-runs auth/phase/consent and lands
  // them on the right screen. The Continue button does the same immediately.
  useEffect(() => {
    if (!weekEnded) return;
    const t = window.setTimeout(() => navigate('/play', { replace: true }), 6000);
    return () => window.clearTimeout(t);
  }, [weekEnded, navigate]);

  const handleIframeLoad = () => {
    if (!latestSessionRef.current) return;
    if (!firstLoadDoneRef.current) {
      firstLoadDoneRef.current = true;
      postSessionToGame(latestSessionRef.current);
      return;
    }
    // The iframe re-loaded (crash recovery, in-game reload, restored tab).
    // The previous session's tokens may have rotated — mint fresh instead of
    // re-posting a stale snapshot.
    void mintAndPostSession();
  };

  if (!session) return null;

  // Weekly boundary hit while in-game: render the interstitial INSTEAD of the
  // iframe. Dropping the iframe from the tree unmounts it, which stops the game
  // immediately (the proactive boot). The player is routed onward to re-consent.
  if (weekEnded) {
    const endedMessage =
      eventState.phase === 'POST_EVENT'
        ? "Gamerhood Season 5 has wrapped. Thanks for playing!"
        : 'A new week has started. Review this week’s rules to keep playing.';
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-black px-6 text-center">
        <h1 className="font-['Orbitron',sans-serif] text-[clamp(20px,5vw,32px)] uppercase tracking-[0.1em] text-white">
          This week has wrapped
        </h1>
        <p className="max-w-[440px] font-['Arial',sans-serif] text-[clamp(14px,2vw,18px)] text-white/85">
          {endedMessage}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate('/play', { replace: true })}
          className="min-w-[220px]"
        >
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <iframe
        ref={iframeRef}
        src="/game/index.html"
        title="Jake's Jingle Quest"
        onLoad={handleIframeLoad}
        allow="fullscreen; autoplay; gamepad"
        className="block h-full w-full border-0"
        style={{ height: '100dvh' }}
      />
    </div>
  );
}
